import { DateTime } from 'luxon';
import type {
  InstructionInputDto,
  RequirementLineInputDto,
  StandingOrderDto,
  SupportingArrangementInputDto,
} from '@pams/shared';
import { IST_ZONE } from '@pams/shared';
import {
  atLeastOnePlace,
  DEFAULT_SHIFT,
  mintShiftId,
  type ArrangementDraft,
  type ShiftRow,
} from '@/components/intake/draft';
import type { PlaceValue } from '@/components/intake/PlaceAutocomplete';
import { istNow } from '@/lib/istNow';
import { instructionIsReal, supportingPersonIsReal } from '@/pages/ArrangementBuilder/autosave';

/**
 * The round-the-clock form's values, and the watched projection every shared section reads.
 *
 * ⚠️ THE DRAFT IS AN `ArrangementDraft`, DELIBERATELY. `PlacesSection` and `ForceGrid` are
 * the SAME components the PA intake renders, and they read that shape. Inventing a second,
 * nearly identical draft type would mean either forking those components — which is exactly
 * the failure the section registry exists to prevent — or a mapping layer that has to be
 * kept in step with two forms at once.
 *
 * The fields a standing order has no use for (`paDate`, the recurrence, the command roster)
 * are filled with values that mean "not applicable here", and no RTC section reads them.
 */
export type StandingOrderEndsKind = 'NEVER' | 'ON_DATE';

export type StandingOrderFormValues = {
  policyId?: string;
  title?: string;
  locations: PlaceValue[];
  shifts: ShiftRow[];
  startDate?: DateTime;
  /** ⚠️ WHEN IT STOPS, and `NEVER` is a real answer rather than a missing one. */
  endsKind: StandingOrderEndsKind;
  endDate?: DateTime;
  requirementLines: RequirementLineInputDto[];
  instructions: InstructionInputDto[];
  supportingArrangements: SupportingArrangementInputDto[];
};

/**
 * ⚠️ THREE RELIEFS, SO THE BAR IS FULL AND GREEN ON FIRST PAINT.
 *
 * An operator learns what the coverage bar means before they can break it, which is a much
 * better first lesson than an empty bar they have to fill to discover the rule. Changing a
 * time is one keystroke; understanding a blank bar is not.
 */
export const DEFAULT_RELIEFS = (): ShiftRow[] => [
  { id: mintShiftId(), startTime: '08:00', endTime: '16:00' },
  { id: mintShiftId(), startTime: '16:00', endTime: '00:00' },
  { id: mintShiftId(), startTime: '00:00', endTime: '08:00' },
];

export const emptyStandingOrder = (): StandingOrderFormValues => ({
  startDate: istNow(),
  locations: [{ placeId: null, name: '' }],
  shifts: DEFAULT_RELIEFS(),
  endsKind: 'NEVER',
  requirementLines: [],
  instructions: [],
  supportingArrangements: [],
});

export const fromStandingOrder = (order: StandingOrderDto): StandingOrderFormValues => ({
  policyId: order.policyId,
  title: order.title,
  /* ⚠️ [D4.5 §2b] THE IDENTICAL DEFECT, ONE FORM OVER, AND THE BRIEF DID NOT NAME IT.
     A standing order saved with no place reopened with no input either. */
  locations: atLeastOnePlace(order.locations),
  shifts:
    order.shifts.length > 0
      ? order.shifts.map((shift) => ({
          id: shift.id,
          name: shift.name,
          startTime: shift.startTime,
          endTime: shift.endTime,
        }))
      : [DEFAULT_SHIFT()],
  startDate: DateTime.fromISO(order.startDate, { zone: IST_ZONE }),
  endsKind: order.endDate === null ? 'NEVER' : 'ON_DATE',
  ...(order.endDate === null ? {} : { endDate: DateTime.fromISO(order.endDate, { zone: IST_ZONE }) }),
  requirementLines: order.requirementLines.map((line) => ({
    postTypeId: line.postTypeId,
    vehicleTypeId: line.vehicleTypeId,
    adHocLabel: line.adHocLabel,
    requiredCount: line.requiredCount,
    allocations: line.allocations.map((allocation) => ({
      divisionOrgUnitId: allocation.divisionOrgUnitId,
      unitOrgUnitId: allocation.unitOrgUnitId,
      count: allocation.count,
    })),
  })),
  instructions: order.instructions.map((row) => ({ type: 'LINE' as const, text: row.text })),
  supportingArrangements: order.supportingArrangements.map((row) => ({
    personName: row.personName,
    responsibilities: [...row.responsibilities],
  })),
});

/** The wire body. `null` end date IS "runs until ended" — see the schema. */
export const toStandingOrderBody = (values: StandingOrderFormValues): Record<string, unknown> => ({
  policyId: values.policyId,
  title: values.title?.trim() ?? '',
  locations: (values.locations ?? [])
    .filter((location) => location.name.trim().length > 0)
    .map((location) => ({ placeId: location.placeId, name: location.name.trim() })),
  shifts: (values.shifts ?? [])
    .filter((shift) => shift.startTime !== '' && shift.endTime !== '')
    .map((shift) => ({ id: shift.id, name: shift.name ?? null, startTime: shift.startTime, endTime: shift.endTime })),
  startDate: values.startDate?.toISODate() ?? undefined,
  endDate: values.endsKind === 'ON_DATE' ? (values.endDate?.toISODate() ?? null) : null,
  requirementLines: (values.requirementLines ?? []).filter((line) => line.requiredCount > 0),
  /*
   * ⚠️ [C5c §8e] BLANK ROWS DROPPED, AS THE PA BUILDER DROPS THEM. This form renders the same
   * `ExtrasSection`, and `text`/`personName` are `.min(1)` on the wire, so "Add line" or "Add person"
   * left unfilled 422'd the save. The predicates are the arming check's own.
   */
  instructions: (values.instructions ?? []).filter(instructionIsReal),
  supportingArrangements: (values.supportingArrangements ?? []).filter(supportingPersonIsReal),
});

/**
 * ⚠️ THE SAME `ArrangementDraft` THE SHARED SECTIONS READ, and every value in it is
 * JSON-serialisable — `toDraft`'s header records why that is load-bearing rather than tidy.
 */
export const toStandingOrderDraft = (raw: unknown): ArrangementDraft & { endsKindRtc: StandingOrderEndsKind } => {
  const values = (raw ?? {}) as Partial<StandingOrderFormValues>;
  const locations = values.locations ?? [];
  const names = locations.map((location) => location.name.trim()).filter((name) => name.length > 0);
  const lines = values.requirementLines ?? [];

  return {
    /*
     * ⚠️ [C3a §6] ALWAYS `null` HERE, AND THAT IS STRUCTURAL RATHER THAN A DEFAULT.
     *
     * A round-the-clock order is SHIFTS by definition — `StandingOrderBuilder/components/
     * Sections.tsx` refuses to offer "no fixed time" at all — so there is no whole-day branch for
     * a reporting time to lead. The field exists on `ArrangementDraft` for the PA intake's cards,
     * and the compiler asking for it here is the shared type working.
     */
    reportingTime: null,
    /* [STAND-DOWN] `null` for the same structural reason — a round-the-clock order has shifts. */
    standDownTime: null,
    /* [C4a §4] `null` — a zone is a duty point's, and a round-the-clock order has none to offer. */
    zone: null,
    /* [C5c §4] And its sector, for the same reason. */
    sector: null,
    policyId: values.policyId ?? '',
    /*
     * ⚠️ [amended C1a] EMPTY, AND THAT IS THE HONEST ANSWER RATHER THAN A PLACEHOLDER.
     *
     * These two exist for the PA intake's duty point cards, where a policy can publish a
     * new version while a card sits open (C1a §2). A standing order names a policy and no
     * version at all — the generator resolves one per generated arrangement, at generation
     * time — so there is no provenance to carry and nothing on this screen reads them.
     */
    policyVersionId: '',
    columnsFromVersionId: '',
    paDate: values.startDate?.toISODate() ?? '',
    title: values.title ?? '',
    locationNames: names,
    placeCount: names.length,
    /* ⚠️ Always a route: one generated arrangement carries exactly one duty. */
    oneAfterAnother: true,
    timingKind: 'MULTIPLE',
    recurrenceKind: 'DATE_RANGE',
    shifts: values.shifts ?? [],
    weekdays: [],
    startDate: values.startDate?.toISODate() ?? '',
    endsKind: 'ON_DATE',
    endDate: values.endDate?.toISODate() ?? '',
    occurrences: undefined,
    /* ⚠️ ALWAYS EMPTY. A standing order captures no command, and there is no field for it. */
    commandEntries: [],
    requirementLines: lines,
    requirementTotals: lines.map((line) => line.requiredCount),
    /* ⚠️ [C5c §8e] Real lines only — the checklist must not tick for a line the save drops. */
    instructionCount: (values.instructions ?? []).filter(instructionIsReal).length,
    endsKindRtc: values.endsKind ?? 'NEVER',
  };
};
